import { useState } from 'react';
import { AlertTriangle, Loader2, Play, StopCircle } from 'lucide-react';
import type { ShowStop } from '../../services/events';
import type { QueueLevel } from '../../utils/localFileLevels';

interface ShowStoppedCardProps {
  showStop: ShowStop;
  songTitle: string | null;
  playerLabel: string;
  level: QueueLevel;
  eventEnded?: boolean;
  onResume: () => Promise<void>;
}

// Bridge paused the player after the song the DJ marked as the last one (ITER-175); eventEnded when auto-end closed it (ITER-199)
export function ShowStoppedCard({ showStop, songTitle, playerLabel, level, eventEnded = false, onResume }: ShowStoppedCardProps) {
  const [resuming, setResuming] = useState(false);

  const handleResume = async () => {
    setResuming(true);
    try {
      await onResume();
    } finally {
      setResuming(false);
    }
  };

  return (
    <div
      data-testid="show-stopped-card"
      className="p-5 bg-gray-50 dark:bg-gray-800/60 border-2 border-gray-300 dark:border-gray-600 rounded-xl"
    >
      <div className="flex items-start gap-4">
        <div className="p-3 bg-gray-200 dark:bg-gray-700 rounded-full flex-shrink-0">
          <StopCircle className="w-6 h-6 text-gray-700 dark:text-gray-200" />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {eventEnded ? 'Event ended after last song' : 'Stopped after last song'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {songTitle ? <>&ldquo;{songTitle}&rdquo; was the last song. </> : 'The last song has ended. '}
            Attendees got the end-of-show thank-you.
            {eventEnded && ' The event is closed.'}
          </p>
          {!showStop.playerPaused && (
            <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Bridge couldn&rsquo;t pause {playerLabel}. Pause it yourself.</span>
            </p>
          )}
          <div className="pt-1">
            <button
              type="button"
              onClick={handleResume}
              disabled={resuming}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-60"
            >
              {resuming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {eventEnded ? 'Reopen and resume' : 'Resume'}
            </button>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              {eventEnded
                ? level === 'bridge'
                  ? `Reopens the event, plays the next song in ${playerLabel} and turns Last Song off.`
                  : 'Reopens the event and turns Last Song off. Start the next song when you’re ready.'
                : level === 'bridge'
                  ? `Plays the next song in ${playerLabel} and turns Last Song off.`
                  : 'Turns Last Song off. Start the next song when you’re ready.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
